import { Action, Reducer } from 'redux';
import { AppThunkAction } from '../..';
import { CategoryCountRequest } from '../../../Interfaces/Assets/CategoryCountRequest';
import { getRequestOptions, postRequestOptions } from '../../../utils/utils';
import { FetchError } from '../../../Interfaces/FetchError';
import { NodeTypeCounts } from '../../../Interfaces/Assets/NodeTypeCounts';
import {
  NodeConfigInfo,
  NodeConfigQueryResult,
} from '../../../Interfaces/Config/NodeConfigInfo';
import {
  ConfigSetting,
  UpdateConfigSetting,
} from '../../../Interfaces/Config/ConfigSetting';
import { ConfigRoot } from '../../../Interfaces/Config/FeatureInfo';
import {
  FeatureFileFolderPath,
  FeatureFilePath,
} from '../../../Interfaces/Config/FeatureFileFolderPath';
import { ServerUploadResult } from '../../../Interfaces/Config/ServerUploadResult';
import { ApiEnums } from '../../../Interfaces/ApiEnums';

export enum ConfigSettingsActionTypes {
  RESET_STATE = 'RESET_STATE',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',

  REQUEST_CONFIG_SETTINGS_BY_NODE_UUID = 'REQUEST_CONFIG_SETTINGS_BY_NODE_UUID',
  RECEIVED_CONFIG_SETTINGS_BY_NODE_UUID = 'RECEIVED_CONFIG_SETTINGS_BY_NODE_UUID',

  REQUEST_CONFIG_SETTINGS_BY_NODE_ID_LIST = 'REQUEST_CONFIG_SETTINGS_BY_NODE_ID_LIST',
  RECEIVED_CONFIG_SETTINGS_BY_NODE_ID_LIST = 'RECEIVED_CONFIG_SETTINGS_BY_NODE_ID_LIST',

  EXPORT_FILTERED_ASSETS_CONFIG = 'EXPORT_FILTERED_ASSETS_CONFIG',

  REQUEST_NODES_CONFIG_BY_PAGE = 'REQUEST_NODES_CONFIG_BY_PAGE',
  RECEIVED_NODES_CONFIG_BY_PAGE = 'RECEIVED_NODES_CONFIG_BY_PAGE',
  RECEIVED_NODES_JSON_CONFIG_BY_PAGE = 'RECEIVED_NODES_JSON_CONFIG_BY_PAGE',

  RESET_NODES_CONFIG_RESULT = 'RESET_NODES_CONFIG_RESULT',

  REQUEST_CONFIG_CATEGORY_LIST = ' REQUEST_CONFIG_CATEGORY_LIST',
  RECEIVED_CONFIG_CATEGORY_LIST = 'RECEIVED_CONFIG_CATEGORY_LIST',

  REQUEST_CONFIG_NAME_LIST = 'REQUEST_CONFIG_NAME_LIST',
  RECEIVED_CONFIG_NAME_LIST = 'RECEIVED_CONFIG_NAME_LIST',

  REQUEST_CONFIG_VALUES = 'REQUEST_CONFIG_VALUES',
  RECEIVED_CONFIG_VALUES = 'RECEIVED_CONFIG_VALUES',

  REQUEST_CONFIG_FEATURES = 'REQUEST_CONFIG_FEATURES',
  RECEIVED_CONFIG_FEATURES = 'RECEIVED_CONFIG_FEATURES',

  RESET_FILTERED_ASSETS_CONFIG = 'RESET_FILTERED_ASSETS_CONFIG',

  RECEIVED_NODE_CONFIG_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_NODE_CONFIG_HTTP_RESPONSE_ERROR_CODE',
  RESET_NODE_CONFIG_HTTP_RESPONSE_FETCH_ERROR = 'RESET_NODE_CONFIG_HTTP_RESPONSE_FETCH_ERROR',

  REQUEST_EDIT_CONFIG_SETTINGS = 'REQUEST_EDIT_CONFIG_SETTINGS',
  RECEIVED_EDIT_CONFIG_SETTINGS_STATUS = 'RECEIVED_EDIT_CONFIG_SETTINGS_STATUS',

  REQUEST_CONFIG_NODE_IMAGE = 'REQUEST_CONFIG_NODE_IMAGE',
  RECEIVED_CONFIG_NODE_IMAGE = 'RECEIVED_CONFIG_NODE_IMAGE',

  REQUEST_FILE_SERVER_UPLOAD = 'REQUEST_FILE_SERVER_UPLOAD',
  RECEIVED_FILE_SERVER_UPLOAD_STATUS = 'RECEIVED_FILE_SERVER_UPLOAD_STATUS',

  SET_FILE_SERVER_UPLOAD_STATUS = 'SET_FILE_SERVER_UPLOAD_STATUS',

  REQUEST_FEATURE_FILE_FOLDER_PATH = 'REQUEST_FEATURE_FILE_FOLDER_PATH',
  RECEIVED_FEATURE_FILE_FOLDER_PATH = 'RECEIVED_FEATURE_FILE_FOLDER_PATH',

  RECEIVED_FEATURE_FILE_PATH = 'RECEIVED_FEATURE_FILE_PATH',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface ConfigSettingsState {
  unauthorizedRequest: boolean;
  isFetchingNodesSettings: boolean;
  isFetchingConfigSettingsInfo: boolean;
  configSettingsInfo: ConfigSetting[];
  filteredAssetsData: NodeConfigInfo[];
  nodesByPage: NodeConfigQueryResult;
  jsonNodesByPage: NodeConfigQueryResult;
  siteNumbers: string[];
  isFetchingAssetCategories: boolean;
  assetCategories: string[];
  isFetchingAssetNames: boolean;
  assetNames: Map<string, string[]>;
  divisions: string[];
  nodeTypes: string[];
  clusters: string[];
  laneNumbers: string[];
  assetValues: Map<CategoryCountRequest, string[]>;
  fetchError: FetchError;
  configSettingsByIDList: ConfigSetting[];
  isFetchingNodeImage: boolean;
  nodeImageUrl: string;
  isFetchingConfigSettingsByIDListInfo: boolean;
  isFetchingFeaturesConfig: boolean;
  featuresConfig: ConfigRoot;
  isUploadingFileToServer: boolean;
  fileUploadToServerStatus: ServerUploadResult;
  isFetchingFeatureFileFolderPath: boolean;
  featureFileFolderPath: FeatureFileFolderPath;
  featureFilePath: FeatureFilePath;
  configValues: Map<CategoryCountRequest, string[]>;
  isFetchingConfigValues: boolean;
  isUpdatingConfigSettings: boolean;
  editConfigStatus: boolean | undefined;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface ResetStateAction {
  type: ConfigSettingsActionTypes.RESET_STATE;
}

interface UnauthorizedRequestAction {
  type: ConfigSettingsActionTypes.UNAUTHORIZED_REQUEST;
}

interface RequestConfigSettingsInfoAction {
  type: ConfigSettingsActionTypes.REQUEST_CONFIG_SETTINGS_BY_NODE_UUID;
}

interface ReceivedConfigSettingsInfoAction {
  type: ConfigSettingsActionTypes.RECEIVED_CONFIG_SETTINGS_BY_NODE_UUID;
  uuid: string;
  configSettingsInfo: ConfigSetting[];
}

interface RequestNodeConfigSettingsByNodeIDListAction {
  type: ConfigSettingsActionTypes.REQUEST_CONFIG_SETTINGS_BY_NODE_ID_LIST;
}

interface ReceivedNodeConfigSettingsByNodeIDListAction {
  type: ConfigSettingsActionTypes.RECEIVED_CONFIG_SETTINGS_BY_NODE_ID_LIST;
  configSettingsByNodeIDList: ConfigSetting[];
}

interface ExportFilteredAssetsAction {
  type: ConfigSettingsActionTypes.EXPORT_FILTERED_ASSETS_CONFIG;
  filteredNodesWithAsset: NodeConfigInfo[];
}

interface RequestNodesByPageAction {
  type: ConfigSettingsActionTypes.REQUEST_NODES_CONFIG_BY_PAGE;
}

interface ReceivedNodesByPageAction {
  type: ConfigSettingsActionTypes.RECEIVED_NODES_CONFIG_BY_PAGE;
  nodesByPage: NodeConfigQueryResult;
}

interface ReceivedJsonNodesByPageAction {
  type: ConfigSettingsActionTypes.RECEIVED_NODES_JSON_CONFIG_BY_PAGE;
  jsonNodesByPage: NodeConfigQueryResult;
}

interface ResetNodesByPageResultAction {
  type: ConfigSettingsActionTypes.RESET_NODES_CONFIG_RESULT;
}

interface RequestAssetCategoryListAction {
  type: ConfigSettingsActionTypes.REQUEST_CONFIG_CATEGORY_LIST;
  refreshing: boolean;
}

interface GetAssetCategoryListAction {
  type: ConfigSettingsActionTypes.RECEIVED_CONFIG_CATEGORY_LIST;
  assetCategories: string[];
}

interface RequestAssetNameListAction {
  type: ConfigSettingsActionTypes.REQUEST_CONFIG_NAME_LIST;
  refreshing: boolean;
}

interface GetAssetNameListAction {
  type: ConfigSettingsActionTypes.RECEIVED_CONFIG_NAME_LIST;
  assetNames: string[];
  category: string;
}

interface ResetFilteredAssetsAction {
  type: ConfigSettingsActionTypes.RESET_FILTERED_ASSETS_CONFIG;
}

interface ReceivedErrorCode {
  type: ConfigSettingsActionTypes.RECEIVED_NODE_CONFIG_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: ConfigSettingsActionTypes.RESET_NODE_CONFIG_HTTP_RESPONSE_FETCH_ERROR;
}

interface RequestEditConfigSettingsAction {
  type: ConfigSettingsActionTypes.REQUEST_EDIT_CONFIG_SETTINGS;
  isUpdatingConfigSettings: boolean;
}

interface RequestConfigNodeImageAction {
  type: ConfigSettingsActionTypes.REQUEST_CONFIG_NODE_IMAGE;
}

interface ReceivedConfigNodeImageAction {
  type: ConfigSettingsActionTypes.RECEIVED_CONFIG_NODE_IMAGE;
  imageUrl: string;
}

interface ReceivedEditConfigSettingsStatus {
  type: ConfigSettingsActionTypes.RECEIVED_EDIT_CONFIG_SETTINGS_STATUS;
  status: boolean;
}

interface RequestConfigFeatures {
  type: ConfigSettingsActionTypes.REQUEST_CONFIG_FEATURES;
  refreshing: boolean;
}

interface ReceivedConfigFeatures {
  type: ConfigSettingsActionTypes.RECEIVED_CONFIG_FEATURES;
  featuresConfig: ConfigRoot;
}

interface RequestFileUploadToServer {
  type: ConfigSettingsActionTypes.REQUEST_FILE_SERVER_UPLOAD;
}

interface ReceivedFileUploadToServer {
  type: ConfigSettingsActionTypes.RECEIVED_FILE_SERVER_UPLOAD_STATUS;
  FileUploadToServerStatus: ServerUploadResult;
}

export interface SetFileUploadToServerStatus {
  type: ConfigSettingsActionTypes.SET_FILE_SERVER_UPLOAD_STATUS;
  fileUploadToServerStatus: ServerUploadResult;
}

interface RequestFeatureFileFolderPath {
  type: ConfigSettingsActionTypes.REQUEST_FEATURE_FILE_FOLDER_PATH;
  isFetching: boolean;
}

interface ReceivedFeatureFileFolderPath {
  type: ConfigSettingsActionTypes.RECEIVED_FEATURE_FILE_FOLDER_PATH;
  featureFileFolderPath: FeatureFileFolderPath;
}

interface ReceivedFilePathAction {
  type: ConfigSettingsActionTypes.RECEIVED_FEATURE_FILE_PATH;
  filePath: FeatureFilePath;
}

interface RequestConfigValuesAction {
  type: ConfigSettingsActionTypes.REQUEST_CONFIG_VALUES;
  refreshing: boolean;
}

interface ReceivedConfigValuesAction {
  type: ConfigSettingsActionTypes.RECEIVED_CONFIG_VALUES;
  categoryCountRequest: CategoryCountRequest;
  values: string[];
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ResetStateAction
  | UnauthorizedRequestAction
  | RequestConfigSettingsInfoAction
  | ReceivedConfigSettingsInfoAction
  | ExportFilteredAssetsAction
  | RequestNodesByPageAction
  | ReceivedNodesByPageAction
  | ReceivedJsonNodesByPageAction
  | ResetNodesByPageResultAction
  | RequestAssetCategoryListAction
  | GetAssetCategoryListAction
  | RequestAssetNameListAction
  | GetAssetNameListAction
  | ResetFilteredAssetsAction
  | ReceivedErrorCode
  | ResetHttpResponseError
  | RequestNodeConfigSettingsByNodeIDListAction
  | ReceivedNodeConfigSettingsByNodeIDListAction
  | RequestEditConfigSettingsAction
  | ReceivedEditConfigSettingsStatus
  | RequestConfigNodeImageAction
  | ReceivedConfigNodeImageAction
  | RequestConfigFeatures
  | ReceivedConfigFeatures
  | RequestFileUploadToServer
  | ReceivedFileUploadToServer
  | SetFileUploadToServerStatus
  | RequestFeatureFileFolderPath
  | ReceivedFeatureFileFolderPath
  | ReceivedFilePathAction
  | RequestConfigValuesAction
  | ReceivedConfigValuesAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: ConfigSettingsActionTypes.UNAUTHORIZED_REQUEST,
    });
  } else {
    dispatch({
      type: ConfigSettingsActionTypes.RECEIVED_NODE_CONFIG_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }
  return false;
};

const dispatchReceivedFileUploadStatus = (
  result: ServerUploadResult,
  dispatch: any,
) => {
  dispatch({
    type: ConfigSettingsActionTypes.RECEIVED_FILE_SERVER_UPLOAD_STATUS,
    FileUploadToServerStatus: result,
  });
};

export const actionCreators = {
  resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (!appState) return;

    console.log(`Dispatching ${ConfigSettingsActionTypes.RESET_STATE}`);

    dispatch({
      type: ConfigSettingsActionTypes.RESET_STATE,
    });
  },

  getConfigSettingsByNodeUuid:
    (uuid: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.REQUEST_CONFIG_SETTINGS_BY_NODE_UUID}`,
      );
      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_CONFIG_SETTINGS_BY_NODE_UUID,
      });

      const response = await fetch(
        `config/settings/GetConfigSettingsByNodeUuid/${uuid}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ConfigSetting[];

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_CONFIG_SETTINGS_BY_NODE_UUID}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_CONFIG_SETTINGS_BY_NODE_UUID,
        uuid: uuid,
        configSettingsInfo: result,
      });
    },

  exportFilteredAssets:
    (configType: ApiEnums.ConfigSettingsType): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const filter =
        configType === ApiEnums.ConfigSettingsType.RegistrySetting
          ? appState.userSettingsReducer.nodesConfigFilters
          : appState.userSettingsReducer.nodesJsonConfigFilters;

      const response = await fetch(
        `config/settings/GetFilteredNodesWithConfigSettings/${configType}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          filter,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeConfigInfo[];

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.EXPORT_FILTERED_ASSETS_CONFIG}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.EXPORT_FILTERED_ASSETS_CONFIG,
        filteredNodesWithAsset: result,
      });
    },

  getNodesByPage:
    (configType: ApiEnums.ConfigSettingsType): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.REQUEST_NODES_CONFIG_BY_PAGE}`,
      );
      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_NODES_CONFIG_BY_PAGE,
      });

      let skip = appState.userSettingsReducer.nodesConfigGridDataState
        .skip as number;
      let pageSize = appState.userSettingsReducer.nodesConfigGridDataState
        .take as number;

      let currentNodesLength =
        appState.configSettingsReducer.nodesByPage.Result.length;

      let pageNumber = Math.floor(skip / pageSize) + 1;

      if (currentNodesLength === 0 || skip > currentNodesLength) {
        if (currentNodesLength > 1) {
          dispatch({
            type: ConfigSettingsActionTypes.RESET_NODES_CONFIG_RESULT,
          });
        }
        pageSize = pageNumber * pageSize;
        pageNumber = 1;
      }

      const nodeQueryInfo = {
        PageNumber: pageNumber,
        PageSize: pageSize,
        NodeFilters:
          configType === ApiEnums.ConfigSettingsType.RegistrySetting
            ? appState.userSettingsReducer.nodesConfigFilters
            : appState.userSettingsReducer.nodesJsonConfigFilters,
      };

      const response = await fetch(
        `config/settings/GetAllNodeConfigByPage/${configType}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          nodeQueryInfo,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeConfigQueryResult;

      if (configType === ApiEnums.ConfigSettingsType.RegistrySetting)
        dispatch({
          type: ConfigSettingsActionTypes.RECEIVED_NODES_CONFIG_BY_PAGE,
          nodesByPage: result,
        });
      else
        dispatch({
          type: ConfigSettingsActionTypes.RECEIVED_NODES_JSON_CONFIG_BY_PAGE,
          jsonNodesByPage: result,
        });
    },

  resetNodesResult:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RESET_NODES_CONFIG_RESULT}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RESET_NODES_CONFIG_RESULT,
      });
    },

  getNodeConfigCategoryList:
    (configType: ApiEnums.ConfigSettingsType): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_CONFIG_CATEGORY_LIST,
        refreshing: true,
      });
      const response = await fetch(
        `config/settings/GetSettingCategories/${configType}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_CONFIG_CATEGORY_LIST}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_CONFIG_CATEGORY_LIST,
        assetCategories: result,
      });
    },

  getAssetNameList:
    (
      configType: ApiEnums.ConfigSettingsType,
      assetCategory: string,
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_CONFIG_NAME_LIST,
        refreshing: true,
      });
      const response = await fetch(
        `config/settings/GetSettingNames/${configType}/${assetCategory}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_CONFIG_NAME_LIST}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_CONFIG_NAME_LIST,
        assetNames: result,
        category: assetCategory,
      });
    },

  getConfigValues:
    (
      configType: ApiEnums.ConfigSettingsType,
      categoryCountRequest: CategoryCountRequest,
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_CONFIG_VALUES,
        refreshing: true,
      });

      const response = await fetch(
        `config/settings/GetConfigValues/${configType}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          categoryCountRequest,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_CONFIG_VALUES}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_CONFIG_VALUES,
        categoryCountRequest: categoryCountRequest,
        values: result,
      });
    },

  clearEditConfigSettings:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_EDIT_CONFIG_SETTINGS_STATUS,
        status: false,
      });
    },

  editConfigSettings:
    (
      configType: ApiEnums.ConfigSettingsType,
      updateConfigSetting: UpdateConfigSetting,
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.REQUEST_EDIT_CONFIG_SETTINGS}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_EDIT_CONFIG_SETTINGS,
        isUpdatingConfigSettings: true,
      });

      const response = await fetch(
        `config/settings/EditConfigSettings/${configType}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          updateConfigSetting,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as boolean;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_EDIT_CONFIG_SETTINGS_STATUS}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_EDIT_CONFIG_SETTINGS_STATUS,
        status: result,
      });
    },

  fetchConfigNodeImage:
    (uuid: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatchin ${ConfigSettingsActionTypes.REQUEST_CONFIG_NODE_IMAGE}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_CONFIG_NODE_IMAGE,
      });

      const response = await fetch(
        `config/settings/GetConfigNodeImage/${uuid}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_CONFIG_NODE_IMAGE}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_CONFIG_NODE_IMAGE,
        imageUrl: result,
      });
    },

  resetFilteredAssets:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RESET_FILTERED_ASSETS_CONFIG}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RESET_FILTERED_ASSETS_CONFIG,
      });
    },

  resetFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RESET_NODE_CONFIG_HTTP_RESPONSE_FETCH_ERROR}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RESET_NODE_CONFIG_HTTP_RESPONSE_FETCH_ERROR,
      });
    },

  getConfigFeatures:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_CONFIG_FEATURES,
        refreshing: true,
      });

      const response = await fetch(
        `config/features/GetConfigFeatures`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ConfigRoot;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_CONFIG_FEATURES}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_CONFIG_FEATURES,
        featuresConfig: result,
      });
    },
  fetchFeatureFileFolderPath:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.REQUEST_FEATURE_FILE_FOLDER_PATH}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_FEATURE_FILE_FOLDER_PATH,
        isFetching: true,
      });

      const response = await fetch(
        `config/features/GetFeatureFileFolderPath`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as FeatureFileFolderPath;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.RECEIVED_FEATURE_FILE_FOLDER_PATH}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.RECEIVED_FEATURE_FILE_FOLDER_PATH,
        featureFileFolderPath: result,
      });
    },

  uploadFileToServer:
    (serverPath: string, data: object): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ConfigSettingsActionTypes.REQUEST_FILE_SERVER_UPLOAD}`,
      );

      dispatch({
        type: ConfigSettingsActionTypes.REQUEST_FILE_SERVER_UPLOAD,
      });

      var fileUploadServerInfo = {
        Data: JSON.stringify(data),
        FilePath: serverPath,
      };

      try {
        const response = await fetch(
          `config/features/UploadFileToServer`,
          postRequestOptions(
            appState.userSessionReducer.sessionInfo?.Token,
            fileUploadServerInfo,
          ),
        );

        console.log(
          `Dispatching ${ConfigSettingsActionTypes.RECEIVED_FILE_SERVER_UPLOAD_STATUS}`,
        );

        if (!isResponseOk(response, dispatch)) return;

        const result = (await response.json()) as ServerUploadResult;

        dispatchReceivedFileUploadStatus(result, dispatch);
      } catch (ex: any) {
        dispatchReceivedFileUploadStatus(
          { Code: -2, Message: ex.Message },
          dispatch,
        );
      }
    },
};

const initialNodeTypeCounts: NodeTypeCounts = {
  Robots: 0,
  MasterRobots: 0,
  Cashiers: 0,
  MobileAttendants: 0,
};

const initialNodesInfoByPage: NodeConfigQueryResult = {
  PageNumber: 0,
  PageSize: 0,
  TotalCount: 0,
  TotalRows: 0,
  Result: [],
};

const initialJsonNodesInfoByPage: NodeConfigQueryResult = {
  PageNumber: 0,
  PageSize: 0,
  TotalCount: 0,
  TotalRows: 0,
  Result: [],
};

const initialConfigFeatures: ConfigRoot = {
  Features: [],
};

const initialFeatureFileFolderPath: FeatureFileFolderPath = {
  FilePath: '',
};

const initialFeatureFilePath: FeatureFilePath = {
  FilePath: '',
};

const initialServerUploadResult: ServerUploadResult = {
  Code: 0,
  Message: '',
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: ConfigSettingsState = {
  unauthorizedRequest: false,
  isFetchingNodesSettings: false,
  isFetchingConfigSettingsInfo: false,
  configSettingsInfo: [],
  filteredAssetsData: [],
  nodesByPage: initialNodesInfoByPage,
  jsonNodesByPage: initialJsonNodesInfoByPage,
  siteNumbers: [],
  isFetchingAssetCategories: false,
  assetCategories: [],
  isFetchingAssetNames: false,
  assetNames: new Map(),
  divisions: [],
  nodeTypes: [],
  clusters: [],
  laneNumbers: [],
  assetValues: new Map(),
  fetchError: { StatusCode: 0, Message: '' },
  configSettingsByIDList: [],
  isFetchingNodeImage: false,
  nodeImageUrl: '',
  isFetchingConfigSettingsByIDListInfo: false,
  isFetchingFeaturesConfig: false,
  featuresConfig: initialConfigFeatures,
  isUploadingFileToServer: false,
  fileUploadToServerStatus: initialServerUploadResult,
  featureFileFolderPath: initialFeatureFileFolderPath,
  featureFilePath: initialFeatureFilePath,
  isFetchingFeatureFileFolderPath: false,
  configValues: new Map(),
  isFetchingConfigValues: false,
  isUpdatingConfigSettings: false,
  editConfigStatus: undefined,
};

export const reducer: Reducer<ConfigSettingsState> = (
  state: ConfigSettingsState | undefined,
  incomingAction: Action,
): ConfigSettingsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case ConfigSettingsActionTypes.RESET_STATE:
      return unloadedState;

    case ConfigSettingsActionTypes.UNAUTHORIZED_REQUEST:
      return {
        ...state,
        unauthorizedRequest: true,
      };

    case ConfigSettingsActionTypes.REQUEST_CONFIG_SETTINGS_BY_NODE_UUID:
      return {
        ...state,
        isFetchingConfigSettingsInfo: true,
      };
    case ConfigSettingsActionTypes.RECEIVED_CONFIG_SETTINGS_BY_NODE_UUID:
      return {
        ...state,
        isFetchingConfigSettingsInfo: false,
        configSettingsInfo: action.configSettingsInfo,
      };

    case ConfigSettingsActionTypes.REQUEST_CONFIG_SETTINGS_BY_NODE_ID_LIST:
      return {
        ...state,
        isFetchingConfigSettingsByIDListInfo: true,
      };
    case ConfigSettingsActionTypes.RECEIVED_CONFIG_SETTINGS_BY_NODE_ID_LIST:
      return {
        ...state,
        isFetchingConfigSettingsByIDListInfo: false,
        configSettingsByIDList: action.configSettingsByNodeIDList,
      };
    case ConfigSettingsActionTypes.EXPORT_FILTERED_ASSETS_CONFIG:
      return {
        ...state,
        filteredAssetsData: action.filteredNodesWithAsset,
      };
    case ConfigSettingsActionTypes.REQUEST_NODES_CONFIG_BY_PAGE:
      return {
        ...state,
        isFetchingNodesSettings: true,
      };
    case ConfigSettingsActionTypes.RECEIVED_NODES_CONFIG_BY_PAGE:
      return {
        ...state,
        isFetchingNodesSettings: false,
        nodesByPage: action.nodesByPage,
      };
    case ConfigSettingsActionTypes.RECEIVED_NODES_JSON_CONFIG_BY_PAGE:
      return {
        ...state,
        isFetchingNodesSettings: false,
        jsonNodesByPage: action.jsonNodesByPage,
      };
    case ConfigSettingsActionTypes.RESET_NODES_CONFIG_RESULT:
      let resetNodesByPageResult = state.nodesByPage;
      resetNodesByPageResult.Result = [];

      return {
        ...state,
        nodesByPage: resetNodesByPageResult,
        filteredAssetsData: [],
      };
    case ConfigSettingsActionTypes.REQUEST_CONFIG_CATEGORY_LIST:
      return {
        ...state,
        isFetchingAssetCategories: action.refreshing,
      };
    case ConfigSettingsActionTypes.RECEIVED_CONFIG_CATEGORY_LIST:
      return {
        ...state,
        assetCategories: action.assetCategories,
        isFetchingAssetCategories: false,
      };
    case ConfigSettingsActionTypes.REQUEST_CONFIG_NAME_LIST:
      return {
        ...state,
        isFetchingAssetNames: action.refreshing,
      };
    case ConfigSettingsActionTypes.RECEIVED_CONFIG_NAME_LIST:
      let newAssetNames = new Map(state.assetNames);

      if (action.assetNames != null && action.category != undefined) {
        newAssetNames.set(action.category, action.assetNames);
      }

      return {
        ...state,
        assetNames: newAssetNames,
        isFetchingAssetNames: false,
      };
    case ConfigSettingsActionTypes.RESET_FILTERED_ASSETS_CONFIG:
      return {
        ...state,
        filteredAssetsData: [],
      };
    case ConfigSettingsActionTypes.RECEIVED_NODE_CONFIG_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_ERROR_MESSAGE_CONFIG_SETTING',
      };
      return {
        ...state,
        isFetchingNodesSettings: false,
        fetchError: fetchErrorData,
        isFetchingAssetCategories: false,
        isFetchingAssetNames: false,
        isFetchingConfigSettingsByIDListInfo: false,
        isFetchingConfigValues: false,
        isUpdatingConfigSettings: false,
        isUploadingFileToServer: false,
      };
    case ConfigSettingsActionTypes.RESET_NODE_CONFIG_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };
    case ConfigSettingsActionTypes.REQUEST_CONFIG_FEATURES:
      return {
        ...state,
        isFetchingFeaturesConfig: true,
      };
    case ConfigSettingsActionTypes.RECEIVED_CONFIG_FEATURES:
      return {
        ...state,
        featuresConfig: action.featuresConfig,
        isFetchingFeaturesConfig: false,
      };
    case ConfigSettingsActionTypes.SET_FILE_SERVER_UPLOAD_STATUS:
      return {
        ...state,
        fileUploadToServerStatus: action.fileUploadToServerStatus,
      };
    case ConfigSettingsActionTypes.REQUEST_FEATURE_FILE_FOLDER_PATH:
      return {
        ...state,
        isFetchingFeatureFileFolderPath: action.isFetching,
      };
    case ConfigSettingsActionTypes.RECEIVED_FEATURE_FILE_FOLDER_PATH:
      var fullPath = action.featureFileFolderPath.FilePath;
      if (fullPath.includes('\\')) fullPath = fullPath + '\\';
      else fullPath = fullPath + '/';

      action.featureFileFolderPath.FilePath = fullPath;

      return {
        ...state,
        featureFileFolderPath: action.featureFileFolderPath,
        isFetchingFeatureFileFolderPath: false,
      };
    case ConfigSettingsActionTypes.SET_FILE_SERVER_UPLOAD_STATUS:
      return {
        ...state,
        fileUploadToServerStatus: action.fileUploadToServerStatus,
      };
    case ConfigSettingsActionTypes.REQUEST_FILE_SERVER_UPLOAD:
      return {
        ...state,
        fileUploadToServerStatus: { Code: 0, Message: '' },
        isUploadingFileToServer: true,
      };
    case ConfigSettingsActionTypes.RECEIVED_FILE_SERVER_UPLOAD_STATUS:
      return {
        ...state,
        fileUploadToServerStatus: action.FileUploadToServerStatus,
        isUploadingFileToServer: false,
      };
    case ConfigSettingsActionTypes.REQUEST_EDIT_CONFIG_SETTINGS:
      return {
        ...state,
        isUpdatingConfigSettings: action.isUpdatingConfigSettings,
        editConfigStatus: undefined,
      };
    case ConfigSettingsActionTypes.RECEIVED_EDIT_CONFIG_SETTINGS_STATUS:
      return {
        ...state,
        isUpdatingConfigSettings: false,
        editConfigStatus: action.status,
      };
    case ConfigSettingsActionTypes.REQUEST_CONFIG_NODE_IMAGE:
      return {
        ...state,
        isFetchingNodeImage: true,
      };
    case ConfigSettingsActionTypes.RECEIVED_CONFIG_NODE_IMAGE:
      return {
        ...state,
        nodeImageUrl: action.imageUrl,
      };
    case ConfigSettingsActionTypes.RECEIVED_CONFIG_VALUES:
      let configValues = new Map(state.configValues);

      if (action.values != null && action.categoryCountRequest != undefined) {
        configValues.set(action.categoryCountRequest, action.values);
      }

      return {
        ...state,
        configValues: configValues,
        isFetchingConfigValues: false,
      };
    case ConfigSettingsActionTypes.REQUEST_CONFIG_VALUES:
      return {
        ...state,
        isFetchingConfigValues: action.refreshing,
      };
    default:
      break;
  }
  return state;
};
